import {
	reconcileOnboarding,
	updateOnboardingPreferences,
} from "@packages/api-client/client/onboardingController";
import { apiClientFetch } from "@packages/api-client/client-config";
import {
	getOnboardingSnapshotQueryKey,
	getOnboardingSnapshotQueryOptions,
	useUpdateOnboardingPreferences as useUpdateOnboardingPreferencesBase,
} from "@packages/api-client/hooks/onboardingController";
import type {
	GetOnboardingSnapshot200,
	UpdateOnboardingPreferencesMutationRequest,
} from "@packages/api-client/types/onboardingController";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { isOfflineQueued } from "@/lib/offline/defineOfflineMutation";

// Re-export the Kubb key builder so every consumer invalidates the same cache
// entry (frontend CLAUDE.md, "Use Kubb query keys").
export { getOnboardingSnapshotQueryKey };

/**
 * The onboarding snapshot as the guide consumes it. Kubb expands every literal
 * union member into its own alias, so the domain-shaped names live here — the
 * guide components never import a `…Enum7` type.
 */
export type OnboardingSnapshot = GetOnboardingSnapshot200;

/** The `eligible: true` arm — the only one carrying guide state. */
export type EligibleOnboardingSnapshot = Extract<
	OnboardingSnapshot,
	{ eligible: true }
>;

export type OnboardingGoal =
	EligibleOnboardingSnapshot["availableGoals"][number];
export type OnboardingStep = EligibleOnboardingSnapshot["steps"][number];
export type OnboardingStepId = OnboardingStep["id"];
export type OnboardingStepStatus = OnboardingStep["status"];
export type OnboardingGuideState = EligibleOnboardingSnapshot["guideState"];

/** Narrows the snapshot union; `{ eligible: false }` renders no guide at all. */
export function isOnboardingEligible(
	snapshot: OnboardingSnapshot | undefined,
): snapshot is EligibleOnboardingSnapshot {
	return snapshot?.eligible === true;
}

/**
 * The guide reads the snapshot on every organization home render, so it is
 * cached like the rest of the home data rather than refetched per mount. It is
 * derived server-side from domain tables, so a short staleTime is enough —
 * qualifying writes invalidate it explicitly.
 */
export const onboardingSnapshotQueryOptions = (orgId: string) => ({
	...getOnboardingSnapshotQueryOptions(orgId),
	staleTime: 60 * 1000,
	// The guide is an assist, never a blocker: a snapshot failure must not throw
	// into the home route's error boundary, and a 404/403 will not fix itself.
	throwOnError: false,
	retry: false,
});

export function invalidateOnboardingSnapshot(
	queryClient: QueryClient,
	orgId: string,
): Promise<void> {
	return queryClient.invalidateQueries({
		queryKey: getOnboardingSnapshotQueryKey(orgId),
	});
}

/** Variables every preference write travels with. */
interface UpdateOnboardingPreferencesVariables {
	organizationId: string;
	data: UpdateOnboardingPreferencesMutationRequest;
}

/**
 * The optimistic snapshot for a preference write, or `undefined` when there is
 * nothing to apply. Pure so the "a refresh must not lose the started action"
 * rule is testable without a component (plan §12).
 *
 * Only the two preference fields move. Steps, activation and availability are
 * server-derived facts and are copied through untouched — the client never
 * guesses a milestone, and the server response replaces the whole snapshot a
 * moment later anyway.
 */
function applyOptimisticPreferences(
	previous: OnboardingSnapshot | undefined,
	data: UpdateOnboardingPreferencesMutationRequest,
): OnboardingSnapshot | undefined {
	if (!isOnboardingEligible(previous)) return undefined;
	if (data.guideState === undefined && data.selectedGoal === undefined) {
		return undefined;
	}
	return {
		...previous,
		...(data.guideState !== undefined ? { guideState: data.guideState } : {}),
		...(data.selectedGoal !== undefined
			? { selectedGoal: data.selectedGoal }
			: {}),
	};
}

/**
 * Persists `guideState` / `selectedGoal` / completion acknowledgement.
 *
 * Silent by policy: minimizing a guide is a preference, not an event worth a
 * toast, and the one failure the user must see (409 — the server has not yet
 * derived activation) is rendered inline by the caller.
 *
 * Both preference fields apply optimistically so "Fazer depois" collapses the
 * panel and a started action is remembered immediately; the server response then
 * replaces the whole snapshot, so derived facts (steps, activation) always come
 * from the server, never from a client guess.
 */
export const useUpdateOnboardingPreferences = () => {
	const queryClient = useQueryClient();

	return useUpdateOnboardingPreferencesBase<{
		previous: OnboardingSnapshot | undefined;
	}>({
		mutation: {
			mutationFn: (variables: UpdateOnboardingPreferencesVariables) =>
				updateOnboardingPreferences(variables.organizationId, variables.data, {
					client: apiClientFetch,
				}),
			meta: { silent: true },
			onMutate: async ({ organizationId, data }) => {
				const queryKey = getOnboardingSnapshotQueryKey(organizationId);
				await queryClient.cancelQueries({ queryKey });
				const previous = queryClient.getQueryData<OnboardingSnapshot>(queryKey);

				const optimistic = applyOptimisticPreferences(previous, data);
				if (optimistic) queryClient.setQueryData(queryKey, optimistic);

				return { previous };
			},
			onError: (_error, { organizationId }, context) => {
				const queryKey = getOnboardingSnapshotQueryKey(organizationId);
				if (context?.previous !== undefined) {
					queryClient.setQueryData(queryKey, context.previous);
				}
				queryClient.invalidateQueries({ queryKey });
			},
			onSuccess: (snapshot, { organizationId }) => {
				// The PATCH returns the freshly derived snapshot, so adopt it wholesale
				// rather than refetching.
				queryClient.setQueryData(
					getOnboardingSnapshotQueryKey(organizationId),
					snapshot,
				);
			},
		},
	});
};

/**
 * Ask the server to re-derive activation after a qualifying record was created,
 * then adopt the fresh snapshot (plan §7.5, §9.3, §16).
 *
 * Deliberately a plain function, not a hook: it is called from the `onSuccess`
 * of the five domain create mutations, which already receive the `QueryClient`.
 *
 * Three properties matter and are all enforced here:
 *
 * - **No client-authored completion.** The endpoint takes no domain facts; the
 *   browser cannot supply an activation timestamp or goal (decision record §7).
 * - **Queued is not done (§3.6).** An offline-queued write resolves through the
 *   success path with a sentinel; it has not reached the server, so there is
 *   nothing to reconcile. Its replay is Phase 5's job.
 * - **Query budget (§16).** It only fires when the *cached* snapshot says this
 *   organization is still mid-onboarding. Organizations that never enrolled, or
 *   already activated, pay nothing on every create forever; the hourly
 *   server-side reconciler is the backstop for the uncached case.
 *
 * Fire-and-forget by design — a reconcile failure must never surface on, or
 * roll back, the domain mutation the user actually performed.
 */
export function reconcileOnboardingAfterFirstRecord(
	queryClient: QueryClient,
	orgId: string,
	result?: unknown,
): void {
	if (isOfflineQueued(result)) return;

	const cached = queryClient.getQueryData<OnboardingSnapshot>(
		getOnboardingSnapshotQueryKey(orgId),
	);
	if (!isOnboardingEligible(cached)) return;
	if (cached.activation.status === "REACHED") return;

	reconcileOnboarding(orgId, { client: apiClientFetch })
		.then((snapshot) => {
			queryClient.setQueryData(getOnboardingSnapshotQueryKey(orgId), snapshot);
		})
		.catch(() => {
			// The record itself is safe; only our view of it is stale. Re-read on
			// the next opportunity and let the scheduled reconciler close the gap.
			queryClient.invalidateQueries({
				queryKey: getOnboardingSnapshotQueryKey(orgId),
			});
		});
}

/**
 * Organizations already reconciled after a replay in this browser session. The
 * endpoint is idempotent and append-only, so one successful call per session is
 * enough: it either materialized activation (and every later call is a no-op) or
 * the organization has no qualifying record yet, in which case the *next*
 * qualifying write reconciles again through the online path.
 */
const reconciledAfterReplay = new Set<string>();

/**
 * Called from a qualifying domain write's replay success (plan §12): "after a
 * qualifying replay succeeds, call the server-side reconcile endpoint; never
 * enqueue a client-authored completion event".
 *
 * Deliberately takes no `QueryClient` — `onReplaySuccess` runs inside the
 * registry, outside React. The registered `invalidate` list is what refreshes
 * the guide; this call exists to *materialize* `activated_at` so the measured
 * activation time is the record's, not the hour the backstop job happens to run.
 *
 * Fire-and-forget and deduplicated per organization: a reconnect that replays
 * fifty queued animals must not fire fifty POSTs (§16). A failure releases the
 * guard so the next replay retries, and the hourly reconciler remains the
 * backstop if every attempt is lost.
 */
export function reconcileOnboardingAfterReplay(orgId: string): void {
	if (reconciledAfterReplay.has(orgId)) return;
	reconciledAfterReplay.add(orgId);

	reconcileOnboarding(orgId, { client: apiClientFetch }).catch(() => {
		reconciledAfterReplay.delete(orgId);
	});
}

/** True for the 409 the server returns when activation is not yet derived. */
export function isActivationNotDerivedError(error: unknown): boolean {
	const status = (error as { response?: { status?: unknown } } | null)?.response
		?.status;
	return status === 409;
}
